/**
 * Concrete Error Handlers
 * Implements Chain of Responsibility pattern for error handling
 */

import type { AxiosError, AxiosResponse } from "axios";
import { HttpStatusCode } from "../types";
import { BaseErrorHandler, type ErrorContext } from "./error-handler-chain";
import { handleTokenRefresh } from "../response/handlers/token-refresh-handler";
import { handleRetry } from "../response/handlers/retry-handler";
import { createApiError } from "../errors";

/**
 * Token Refresh Error Handler
 * Handles 401 Unauthorized errors by refreshing tokens
 */
export class TokenRefreshErrorHandler extends BaseErrorHandler {
  priority = 100; // Highest priority - handle auth errors first

  canHandle(error: AxiosError, context: ErrorContext): boolean {
    return (
      error.response?.status === HttpStatusCode.UNAUTHORIZED &&
      !context.originalRequest._retry
    );
  }

  protected async handleError(
    error: AxiosError,
    context: ErrorContext,
  ): Promise<AxiosResponse | null> {
    try {
      const result = await handleTokenRefresh(
        error,
        context.originalRequest,
        context.axiosInstance,
        context.config,
      );
      return result as AxiosResponse;
    } catch {
      // Token refresh failed, pass to next handler
      return null;
    }
  }
}

/**
 * Retry Error Handler
 * Handles retryable errors with exponential backoff
 */
export class RetryErrorHandler extends BaseErrorHandler {
  priority = 50; // Medium priority - handle after token refresh

  canHandle(): boolean {
    // Retry logic is handled in handleRetry function
    // This handler always checks, but handleRetry will determine if retry is needed
    return true; // Always check, but handleRetry will determine if retry is needed
  }

  protected async handleError(
    error: AxiosError,
    context: ErrorContext,
  ): Promise<AxiosResponse | null> {
    const retryResult = await handleRetry(
      error,
      context.originalRequest,
      context.axiosInstance,
      context.config,
    );

    return retryResult as AxiosResponse | null;
  }
}

/**
 * Final Error Handler
 * Transforms error and calls error callbacks
 */
export class FinalErrorHandler extends BaseErrorHandler {
  priority = 0; // Lowest priority - always last

  canHandle(): boolean {
    return true; // Always handles (final fallback)
  }

  protected async handleError(
    error: AxiosError,
    context: ErrorContext,
  ): Promise<AxiosResponse | null> {
    // Create ApiError and call error callbacks
    const apiError = createApiError(error);

    if (context.config.onError) {
      context.config.onError(apiError);
    }

    // Return null to indicate error should be rejected
    return null;
  }
}
